<template>
  <div class="wa-tpl-form">
    <div class="wa-tpl-form__title mb-3">
      {{ isEdit ? "Editar plantilla" : "Nueva plantilla" }}
    </div>

    <b-row>
      <!-- Columna izquierda: formulario -->
      <b-col xs="12" md="7">
        <el-form :model="form" label-position="top" size="small">
          <el-form-item label="Nombre de la plantilla">
            <el-input
              v-model="form.name"
              :disabled="isEdit"
              placeholder="ej: recordatorio_cobro"
              @input="
                form.name = form.name.toLowerCase().replace(/[^a-z0-9_]/g, '_')
              "
            />
            <div class="wa-hint">
              Solo letras minúsculas, números y guion bajo. Sin espacios.
            </div>
          </el-form-item>

          <b-row>
            <b-col xs="12" md="6">
              <el-form-item label="Categoría">
                <el-select v-model="form.category" style="width:100%;">
                  <el-option label="Transaccional (utility)" value="utility" />
                  <el-option label="Marketing" value="marketing" />
                  <el-option label="Autenticación" value="authentication" />
                </el-select>
              </el-form-item>
            </b-col>
            <b-col xs="12" md="6">
              <el-form-item label="Idioma">
                <el-select v-model="form.language" style="width:100%;">
                  <el-option label="Español (es)" value="es" />
                  <el-option label="Español EC (es_EC)" value="es_EC" />
                  <el-option label="English (en)" value="en" />
                </el-select>
              </el-form-item>
            </b-col>
          </b-row>

          <!-- Encabezado -->
          <el-form-item>
            <div
              slot="label"
              class="d-flex align-items-center"
              style="gap:8px;"
            >
              <span>Encabezado (opcional)</span>
              <el-switch
                v-model="headerEnabled"
                active-color="#3ecfb4"
                @change="onHeaderToggle"
              />
            </div>
            <el-input
              v-if="headerEnabled"
              v-model="form.header_content"
              placeholder="Ej: Factura disponible"
              maxlength="60"
              show-word-limit
            />
            <div v-if="headerEnabled" class="wa-hint">
              Aparece en negrita sobre el cuerpo del mensaje.
            </div>
          </el-form-item>

          <el-form-item label="Cuerpo del mensaje">
            <el-input
              v-model="form.body"
              type="textarea"
              :rows="4"
              :placeholder="bodyPlaceholder"
            />
            <div class="wa-hint">
              Usa <code>{{ 1 }}</code
              >, <code>{{ 2 }}</code
              >... para variables dinámicas (secuenciales desde 1).
              <span style="color:#d97706;"
                >El mensaje no puede empezar ni terminar con una variable.</span
              >
            </div>
          </el-form-item>

          <el-form-item label="Pie de página (opcional)">
            <el-input
              v-model="form.footer"
              placeholder="Mydessk — Gestión simple"
              maxlength="60"
              show-word-limit
            />
          </el-form-item>

          <!-- Botones -->
          <el-form-item label="Botones (opcional)">
            <div
              v-for="(btn, i) in form.buttons"
              :key="i"
              class="wa-btn-row mb-2"
            >
              <el-select
                v-model="btn.type"
                size="mini"
                style="width:130px;flex-shrink:0;"
                @change="onBtnTypeChange(i)"
              >
                <el-option label="Respuesta rápida" value="QUICK_REPLY" />
                <el-option label="URL" value="URL" />
                <el-option label="Teléfono" value="PHONE_NUMBER" />
              </el-select>
              <el-input
                v-model="btn.text"
                size="mini"
                placeholder="Texto del botón"
                style="flex:1;"
                maxlength="25"
              />
              <el-input
                v-if="btn.type === 'URL'"
                v-model="btn.url"
                size="mini"
                placeholder="https://..."
                style="flex:1.5;"
              />
              <el-input
                v-if="btn.type === 'PHONE_NUMBER'"
                v-model="btn.phone_number"
                size="mini"
                placeholder="+593..."
                style="flex:1;"
              />
              <el-button
                icon="el-icon-minus"
                circle
                size="mini"
                @click="removeButton(i)"
              />
            </div>
            <el-button
              v-if="form.buttons.length < 3"
              size="mini"
              icon="el-icon-plus"
              @click="addButton"
              style="margin-top:4px;"
            >
              Agregar botón
            </el-button>
            <div class="wa-hint mt-1">
              Máximo 3 botones. Respuesta rápida o acciones (URL / teléfono).
            </div>
          </el-form-item>

          <!-- Variables -->
          <el-form-item label="Variables del mensaje">
            <div class="wa-hint mb-2" style="margin-top:-4px;">
              Indica qué dato del sistema reemplazará cada variable
              <code>&#123;&#123;N&#125;&#125;</code> al enviar (nombre del
              cliente, número de factura, etc.). Esto permite que al usar la
              plantilla se auto-completen los datos por destinatario.
            </div>
            <div
              v-for="(v, i) in form.variables"
              :key="i"
              class="d-flex align-items-center mb-1"
              style="gap:8px;"
            >
              <span class="wa-var-index" v-text="varLabel(i)"></span>
              <el-select
                v-model="form.variables[i]"
                size="mini"
                style="flex:1;"
                filterable
                allow-create
                default-first-option
                placeholder="Selecciona o escribe..."
              >
                <el-option-group label="Datos del sistema">
                  <el-option
                    v-for="t in tokenSuggestions"
                    :key="t.value"
                    :label="t.label"
                    :value="t.value"
                  >
                    <span style="float:left;">{{ t.label }}</span>
                    <span
                      style="float:right;color:#9ca3af;font-size:11px;font-family:monospace;"
                      >{{ t.value }}</span
                    >
                  </el-option>
                </el-option-group>
              </el-select>
              <el-button
                icon="el-icon-minus"
                circle
                size="mini"
                @click="removeVariable(i)"
              />
            </div>
            <el-button
              size="mini"
              icon="el-icon-plus"
              @click="addVariable"
              style="margin-top:4px;"
            >
              Agregar variable
            </el-button>
          </el-form-item>

          <!-- Botones de acción -->
          <div class="d-flex" style="gap:8px;margin-top:8px;">
            <el-button
              :loading="saving"
              style="background:#2d2b55;color:#fff;border:none;border-radius:8px;flex:1;"
              @click="save"
            >
              {{ isEdit ? "Actualizar" : "Crear plantilla" }}
            </el-button>
            <el-button style="border-radius:8px;" @click="$emit('cancel')">
              Cancelar
            </el-button>
          </div>
        </el-form>
      </b-col>

      <!-- Columna derecha: preview -->
      <b-col xs="12" md="5">
        <WhatsappTemplatePreview
          :header="headerEnabled ? form.header_content : ''"
          :body="form.body"
          :footer="form.footer"
          :buttons="form.buttons"
          :sample-variables="form.variables"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import configService from "../../../services/configService";
import WhatsappTemplatePreview from "./WhatsappTemplatePreview";

// Sugerencias de variables del sistema (deben coincidir con
// WhatsAppTokenResolver del backend para resolución automática)
const TOKEN_SUGGESTIONS = [
  { value: "nombre_cliente", label: "Nombre del cliente" },
  { value: "email_cliente", label: "Email del cliente" },
  { value: "telefono_cliente", label: "Teléfono del cliente" },
  { value: "empresa", label: "Tu empresa" },
  { value: "ruc_empresa", label: "RUC de tu empresa" },
  { value: "telefono_empresa", label: "Teléfono de tu empresa" },
  { value: "numero_cotizacion", label: "Número de cotización" },
  { value: "numero_factura", label: "Número de factura" },
  { value: "numero_retencion", label: "Número de retención" },
  { value: "numero_liquidacion", label: "Número de liquidación" },
  { value: "numero_remision", label: "Número de guía de remisión" },
  { value: "destinatario", label: "Destinatario" },
  { value: "monto", label: "Monto / total" },
  { value: "fecha", label: "Fecha actual" },
  { value: "fecha_vencimiento", label: "Fecha de vencimiento" },
  { value: "dias_vencida", label: "Días vencida" }
];

const DEFAULT_FORM = () => ({
  name: "",
  category: "utility",
  language: "es",
  header_type: "none",
  header_content: "",
  body: "",
  footer: "",
  buttons: [],
  variables: []
});

export default {
  name: "WhatsappTemplateForm",
  components: { WhatsappTemplatePreview },
  props: {
    template: { type: Object, default: null }
  },
  data() {
    return {
      form: this.template ? this.buildForm(this.template) : DEFAULT_FORM(),
      headerEnabled: this.template
        ? (this.template.header_type || "none") !== "none"
        : false,
      saving: false,
      bodyPlaceholder:
        "Hola \x7b\x7b1\x7d\x7d, tu factura #\x7b\x7b2\x7d\x7d por $\x7b\x7b3\x7d\x7d está disponible..."
    };
  },
  computed: {
    isEdit() {
      return !!this.template;
    },
    tokenSuggestions() {
      return TOKEN_SUGGESTIONS;
    }
  },
  watch: {
    template(val) {
      this.form = val ? this.buildForm(val) : DEFAULT_FORM();
      this.headerEnabled = val ? (val.header_type || "none") !== "none" : false;
    }
  },
  methods: {
    buildForm(t) {
      return {
        name: t.name || "",
        category: t.category || "utility",
        language: t.language || "es",
        header_type: t.header_type || "none",
        header_content: t.header_content || "",
        body: t.body || "",
        footer: t.footer || "",
        buttons: t.buttons ? JSON.parse(JSON.stringify(t.buttons)) : [],
        variables: t.variables ? [...t.variables] : []
      };
    },

    onHeaderToggle(val) {
      this.form.header_type = val ? "text" : "none";
      if (!val) this.form.header_content = "";
    },

    addButton() {
      if (this.form.buttons.length >= 3) return;
      this.form.buttons.push({ type: "QUICK_REPLY", text: "" });
    },
    removeButton(i) {
      this.form.buttons.splice(i, 1);
    },
    onBtnTypeChange(i) {
      const btn = this.form.buttons[i];
      // Limpiar campos extra al cambiar tipo
      delete btn.url;
      delete btn.phone_number;
      this.$set(this.form.buttons, i, { ...btn });
    },

    varLabel(i) {
      return "\x7b\x7b" + (i + 1) + "\x7d\x7d";
    },
    addVariable() {
      this.form.variables.push("");
    },
    removeVariable(i) {
      this.form.variables.splice(i, 1);
    },

    validateBody(body) {
      if (!body) return "El cuerpo del mensaje es obligatorio.";

      // Meta no permite que el body empiece o termine con una variable {{N}}
      const varPattern = /\{\{\d+\}\}/;
      const trimmed = body.trim();
      if (varPattern.test(trimmed.slice(0, 5))) {
        return "El cuerpo no puede empezar con una variable {{1}}. Agrega texto antes.";
      }
      if (varPattern.test(trimmed.slice(-5))) {
        return "El cuerpo no puede terminar con una variable {{1}}. Agrega texto después.";
      }

      // Verificar que las variables sean secuenciales desde {{1}}
      const vars = [...trimmed.matchAll(/\{\{(\d+)\}\}/g)].map(m =>
        parseInt(m[1])
      );
      const uniqueVars = [...new Set(vars)].sort((a, b) => a - b);
      for (let i = 0; i < uniqueVars.length; i++) {
        if (uniqueVars[i] !== i + 1) {
          return `Las variables deben ser secuenciales desde {{1}}. Falta {{${i +
            1}}}.`;
        }
      }

      return null;
    },

    async save() {
      if (!this.form.name) {
        this.$message.warning("El nombre de la plantilla es obligatorio.");
        return;
      }

      const bodyError = this.validateBody(this.form.body);
      if (bodyError) {
        this.$message.warning(bodyError);
        return;
      }

      this.saving = true;
      try {
        let res;
        if (this.isEdit) {
          res = await configService.updateWhatsAppTemplate(
            this.template.id,
            this.form
          );
        } else {
          res = await configService.createWhatsAppTemplate(this.form);
        }

        if (res.data.success) {
          this.$notifications.success({
            message: this.isEdit
              ? "Plantilla actualizada."
              : "Plantilla creada."
          });
          this.$emit("saved", res.data.data);
        } else {
          this.$notifications.error({
            message: res.data.message || "Error al guardar."
          });
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.wa-tpl-form__title {
  font-size: 15px;
  font-weight: 700;
  color: #2d2b55;
}
.wa-hint {
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
}
.wa-var-index {
  background: #f3f4f6;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}
.wa-btn-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
code {
  background: #f3f4f6;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
}
</style>
